import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import BaseBreadcrumbs from '../BaseBreadcrumbs'
import { useLogout } from '../../features/auth/Logout'
const IconChevron = ({ className = 'w-4 h-4' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
)

const IconCluster = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 7h16M4 12h16M4 17h7"
        />
    </svg>
)

const IconGlobe = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 0c2.5 2.5 4 6 4 9s-1.5 6.5-4 9m0-18c-2.5 2.5-4 6-4 9s1.5 6.5 4 9M3 12h18"
        />
    </svg>
)

const IconServer = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <rect x="3" y="4" width="18" height="6" rx="2" />
        <rect x="3" y="14" width="18" height="6" rx="2" />
        <path d="M7 8h0M7 18h0" />
    </svg>
)

const IconUsers = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 20a4 4 0 0 0-8 0m8-10a4 4 0 1 0-8 0m12 10v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"
        />
    </svg>
)

const IconCog = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m10.325 4.317 1.175-2.03 1.175 2.03A2 2 0 0 0 14.5 5.5l2.267.33-.33 2.267a2 2 0 0 0 .547 1.717l1.6 1.6-1.6 1.6a2 2 0 0 0-.547 1.717l.33 2.267L14.5 18.5a2 2 0 0 0-1.825 1.183l-1.175 2.03-1.175-2.03A2 2 0 0 0 8.5 18.5l-2.267-.33.33-2.267a2 2 0 0 0-.547-1.717l-1.6-1.6 1.6-1.6a2 2 0 0 0 .547-1.717l-.33-2.267L8.5 5.5a2 2 0 0 0 1.825-1.183Z"
        />
        <circle cx="12" cy="12" r="3" />
    </svg>
)

const IconMoon = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        />
    </svg>
)

const IconSun = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v2m0 14v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42M8 12a4 4 0 1 0 8 0 4 4 0 0 0-8 0z"
        />
    </svg>
)

// OpenStack specific icon
const CloudIcon = ({
    className = 'w-5 h-5 mr-2',
    iconColor = 'text-blue-400',
}) => (
    <div
        className={`${className} ${iconColor} flex items-center justify-center`}
    >
        <svg className="w-full h-full" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>
    </div>
)

// ---------------- Helpers ---------------- //
const classNames = (...xs) => xs.filter(Boolean).join(' ')

// menu config (children are links). Cluster group routes will get ?cluster=<id>
const buildMenus = (selectedClusterId) => {
    const withCluster = (path) => (selectedClusterId ? `${path}` : path)

    return [
        {
            key: 'cluster',
            title: 'Cluster Management',
            icon: <IconCluster />,
            children: [
                { label: 'Overview', to: withCluster('/admin/dashboard') },
                { label: 'Computes', to: withCluster('/admin/computes') },
                {
                    label: 'Compute Types',
                    to: withCluster('/admin/compute-types'),
                },
                { label: 'Plans-Flavors', to: withCluster('/admin/plans') },
                {
                    label: 'Plans Categories',
                    to: withCluster('/admin/plan-categories'),
                },
                { label: 'Projects', to: withCluster('/admin/projects') },
                { label: 'Images', to: withCluster('/admin/images') },
                { label: 'Networks', to: withCluster('/admin/network') },
                {
                    label: 'Infrastructures',
                    to: withCluster('/admin/infrastructure-manager'),
                },
            ],
        },

        {
            key: 'global',
            title: 'Global Management',
            icon: <IconGlobe />,
            children: [
                { label: 'Users', to: '/admin/users-manager' },
                { label: 'Regions', to: '/admin/regions' },
                { label: 'Clusters', to: '/admin/clusters' },
                { label: 'Plans', to: '/admin/plans' },
                { label: 'Billing', to: '/admin/billing' },
                { label: 'Support', to: '/admin/support' },
                { label: 'Logging', to: '/admin/logging' },
            ],
        },
    ]
}

export default function SidebarLayout() {
    const location = useLocation()
    const logout = useLogout()

    // theme
    const [darkMode, setDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme')
        if (savedTheme) return savedTheme === 'dark'
        return window.matchMedia('(prefers-color-scheme: dark)').matches
    })
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark')
            localStorage.setItem('theme', 'dark')
        } else {
            document.documentElement.classList.remove('dark')
            localStorage.setItem('theme', 'light')
        }
    }, [darkMode])

    // clusters
    const [clusters, setClusters] = useState([])
    const [selectedCluster, setSelectedCluster] = useState(() => {
        return JSON.parse(localStorage.getItem('selected_cluster'))
    })
    const [clusterQuery, setClusterQuery] = useState('')
    const [loading, setLoading] = useState(true)

    const fetchClusters = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get('/admin/clusters')
            const list = res.data || []

            setClusters(list)
            localStorage.setItem('clusters', JSON.stringify(list))
            if (!selectedCluster && list.length > 0) {
                setSelectedCluster(list[0])
                localStorage.setItem(
                    'selected_cluster',
                    JSON.stringify(list[0])
                )
            }
        } catch (e) {
            console.error('❌ Fetch clusters failed:', e)
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => {
        fetchClusters()
    }, [])

    // sidebar + menus
    const [sidebarOpen, setSidebarOpen] = useState(false) // mobile
    const [openGroups, setOpenGroups] = useState({
        cluster: true,
        global: true,
        clusterPicker: false,
    })
    const menus = useMemo(
        () => buildMenus(selectedCluster?.id),
        [selectedCluster?.id]
    )

    const filteredClusters = useMemo(() => {
        const q = clusterQuery.trim().toLowerCase()
        if (!q) return clusters
        return clusters.filter((c) => c.name?.toLowerCase().includes(q))
    }, [clusterQuery, clusters])

    const toggleGroup = (key) =>
        setOpenGroups((s) => ({ ...s, [key]: !s[key] }))

    // measure header height to offset sidebar/overlay
    const headerRef = useRef(null)
    const [headerH, setHeaderH] = useState(0)
    useEffect(() => {
        const measure = () => {
            if (headerRef.current)
                setHeaderH(headerRef.current.offsetHeight || 0)
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [])

    // --------------- Render --------------- //
    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
            <header
                ref={headerRef}
                className="sticky top-0 z-50 bg-gray-800 text-gray-100 shadow"
            >
                <div className="flex items-center justify-between px-4 py-3">
                    {/* Left: burger + brand */}
                    <div className="flex items-center gap-3">
                        {/* Open sidebar (mobile) */}
                        <button
                            className="md:hidden inline-flex items-center justify-center rounded-lg p-2 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open sidebar"
                        >
                            <IconServer className="w-6 h-6" />
                        </button>

                        {/* Brand */}
                        <div className="flex items-center gap-3">
                            <CloudIcon className="w-8 h-8 text-blue-400" />
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent tracking-tight">
                                Greencloud
                            </h2>
                        </div>
                    </div>

                    {/* Right: Cluster selector + Theme */}

                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <button
                                onClick={() => toggleGroup('clusterPicker')}
                                className="inline-flex items-center justify-between gap-2 rounded-md bg-gray-900 px-3 py-2 text-sm hover:bg-gray-700"
                            >
                                <span className="truncate max-w-[12rem]">
                                    {selectedCluster
                                        ? selectedCluster.name
                                        : loading
                                        ? 'Loading...'
                                        : 'Select Cluster'}
                                </span>
                                <IconChevron
                                    className={classNames(
                                        'w-4 h-4 transition-transform',
                                        openGroups.clusterPicker
                                            ? 'rotate-180'
                                            : 'rotate-0'
                                    )}
                                />
                            </button>
                            {openGroups.clusterPicker && (
                                <div className="absolute right-0 z-50 mt-2 w-72 rounded-lg bg-gray-800 p-3 shadow-lg">
                                    <input
                                        value={clusterQuery}
                                        onChange={(e) =>
                                            setClusterQuery(e.target.value)
                                        }
                                        placeholder="Search cluster..."
                                        className="mb-2 w-full rounded bg-gray-700 px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                    <ul className="max-h-60 space-y-1 overflow-y-auto pr-1">
                                        {filteredClusters.map((c) => (
                                            <li key={c.id}>
                                                <button
                                                    onClick={() => {
                                                        setSelectedCluster(c)
                                                        setOpenGroups((s) => ({
                                                            ...s,
                                                            clusterPicker: false,
                                                        }))
                                                        localStorage.setItem(
                                                            'selected_cluster',
                                                            JSON.stringify(c)
                                                        )
                                                        window.location.reload()
                                                    }}
                                                    className={classNames(
                                                        'w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-700',
                                                        selectedCluster?.id ===
                                                            c.id &&
                                                            'bg-emerald-600 text-white'
                                                    )}
                                                >
                                                    {c.name}
                                                </button>
                                            </li>
                                        ))}
                                        {!loading &&
                                            filteredClusters.length === 0 && (
                                                <li className="px-3 py-2 text-sm text-gray-400">
                                                    No matches
                                                </li>
                                            )}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={(e) => {
                                e.preventDefault()
                                logout()
                            }}
                            className="flex items-center gap-2 space-x-2 p-2 font-medium rounded-md hover:bg-red-100 dark:text-white dark:hover:bg-red-600 transition"
                        >
                            <span className="hidden md:inline-block">
                                Logout
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Sidebar overlay (mobile): starts below header */}
            {sidebarOpen && (
                <div
                    className="fixed left-0 right-0 bottom-0 z-40 md:hidden bg-black/50"
                    style={{ top: headerH }}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar: starts below header */}
            <aside
                className={classNames(
                    'fixed left-0 bottom-0 z-50 w-72 transform bg-gray-900 text-gray-100 shadow-xl transition-transform md:translate-x-0',
                    sidebarOpen
                        ? 'translate-x-0'
                        : '-translate-x-full md:translate-x-0'
                )}
                style={{ top: headerH }}
                aria-label="Sidebar"
            >
                <div className="flex h-full flex-col">
                    {/* Menu groups */}
                    <nav className="flex-1 space-y-4 overflow-y-auto p-4">
                        {menus.map((group) => (
                            <div key={group.key} className="select-none">
                                <button
                                    onClick={() => toggleGroup(group.key)}
                                    className="flex w-full items-center justify-between rounded-md px-2 py-2 hover:bg-gray-800"
                                >
                                    <div className="flex items-center gap-2">
                                        {group.icon}
                                        <span className="text-sm font-semibold">
                                            {group.title}
                                        </span>
                                    </div>
                                    <IconChevron
                                        className={classNames(
                                            'w-4 h-4 transition-transform',
                                            openGroups[group.key]
                                                ? 'rotate-180'
                                                : 'rotate-0'
                                        )}
                                    />
                                </button>
                                {openGroups[group.key] && (
                                    <ul className="mt-1 space-y-1 pl-2">
                                        {group.children.map((item) => (
                                            <li key={item.to}>
                                                <NavLink
                                                    to={item.to}
                                                    className={({ isActive }) =>
                                                        classNames(
                                                            'block rounded-md px-3 py-2 text-sm hover:bg-gray-800',
                                                            isActive ||
                                                                location.pathname ===
                                                                    item.to
                                                                ? 'bg-gray-800 text-emerald-400'
                                                                : 'text-gray-300'
                                                        )
                                                    }
                                                    onClick={() =>
                                                        setSidebarOpen(false)
                                                    }
                                                >
                                                    {item.label}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </nav>
                    {/* Footer actions */}
                    <div className="border-t border-white/10 p-4">
                        <button
                            onClick={() => setDarkMode((d) => !d)}
                            className="flex w-full items-center justify-center gap-2 rounded-md bg-gray-800 px-3 py-2 text-sm hover:bg-gray-700"
                        >
                            {darkMode ? <IconMoon /> : <IconSun />} Toggle theme
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="md:pl-72">
                <div className="mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    )
}
